import { createContext, useContext, useEffect, useState, useCallback, useRef, type ReactNode } from 'react';
import { onlineManager } from '@tanstack/react-query';
import { getQueueCounts, type QueueCounts } from '@/lib/offline-store';
import { processSyncQueue, onSyncQueueChange } from '@/lib/sync-queue';
import { getBaseUrl } from '@/api/client';
import { isDesktopApp } from '@/lib/desktop';

// Captive-portal guard: `navigator.onLine === true` only means there is a
// network interface — not that the API is reachable. We confirm real
// connectivity by pinging `/api/ping` (app.ts), which needs no DB, LLM or
// auth, so a slow or disabled feature never makes the app read offline.
const HEALTH_PING_INTERVAL_MS = 60_000;
const HEALTH_PING_TIMEOUT_MS = 5_000;

async function pingApiHealth(): Promise<boolean> {
  try {
    const response = await fetch(`${getBaseUrl()}/ping`, {
      method: 'GET',
      cache: 'no-store',
      signal: AbortSignal.timeout(HEALTH_PING_TIMEOUT_MS),
    });
    // A captive portal typically answers with its own HTML page —
    // require a successful JSON response from our API.
    const contentType = response.headers.get('content-type') ?? '';
    return response.ok && contentType.includes('application/json');
  } catch {
    return false;
  }
}

interface OfflineContextValue {
  isOnline: boolean;
  isOffline: boolean;
  /**
   * État RÉSEAU brut (navigator.onLine + événements online/offline) —
   * purement informatif. Distinct de `isOnline` (joignabilité de l'API,
   * qui pilote React Query) : sur desktop, l'API locale reste joignable
   * réseau coupé — ce flag permet quand même d'afficher « réseau
   * indisponible » sans rien mettre en pause.
   */
  isNetworkOnline: boolean;
  isSyncing: boolean;
  pendingCount: number;
  /** Entries the server rejected — awaiting manual review (retry/discard). */
  failedCount: number;
  /**
   * Entries withheld from the CURRENT session: queued under another
   * tenant or user, or unattributable (queued before entries carried
   * their tenant and user).
   * Never sent under this session — see `isHeldEntry`.
   */
  heldCount: number;
  /** Timestamp (ms) of the last successful API health ping. */
  lastOnlineAt: number | null;
  syncNow: () => Promise<void>;
}

const OfflineContext = createContext<OfflineContextValue>({
  isOnline: true,
  isOffline: false,
  isNetworkOnline: true,
  isSyncing: false,
  pendingCount: 0,
  failedCount: 0,
  heldCount: 0,
  lastOnlineAt: null,
  syncNow: async () => {},
});

export function useOffline() {
  return useContext(OfflineContext);
}

export function OfflineProvider({ children }: { children: ReactNode }) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isNetworkOnline, setIsNetworkOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [heldCount, setHeldCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastOnlineAt, setLastOnlineAt] = useState<number | null>(null);
  const wasOfflineRef = useRef(false);
  const isSyncingRef = useRef(false);

  const applyCounts = useCallback((counts: QueueCounts) => {
    setPendingCount(counts.pending);
    setFailedCount(counts.failed);
    setHeldCount(counts.held);
  }, []);

  /**
   * Re-read the queue counts. `held` is NOT a property of the stored
   * record — it depends on the session in force (tenant and user), which
   * changes with no queue mutation to signal it (login, token refresh,
   * cabinet switch-tenant). So the counts must be re-derived on a timer
   * as well as on queue changes, or the banner keeps calling withheld
   * work « en attente ».
   */
  const refreshCounts = useCallback(async () => {
    applyCounts(await getQueueCounts());
  }, [applyCounts]);

  const syncNow = useCallback(async () => {
    // Desktop (Electron) : l'API embarquée est sur 127.0.0.1 — le loopback
    // ne dépend d'aucune interface réseau, navigator.onLine ne dit rien.
    if (isSyncingRef.current || (!isDesktopApp() && !navigator.onLine)) return;
    isSyncingRef.current = true;
    setIsSyncing(true);
    try {
      await processSyncQueue();
      await refreshCounts();
    } finally {
      isSyncingRef.current = false;
      setIsSyncing(false);
    }
  }, [refreshCounts]);

  const markOffline = useCallback(() => {
    wasOfflineRef.current = true;
    setIsOnline(false);
    onlineManager.setOnline(false);
  }, []);

  /**
   * Verify real connectivity: browser says online AND the API health
   * ping succeeds. Only then report isOnline=true (and auto-sync if we
   * were offline).
   */
  const verifyConnectivity = useCallback(async () => {
    // Desktop (Electron) : Wi-Fi coupé ⇒ navigator.onLine === false, mais
    // l'API embarquée sur 127.0.0.1 reste joignable (loopback). Court-
    // circuiter ici mettrait TOUTES les requêtes React Query en pause
    // (onlineManager) et l'app ne chargerait plus rien hors connexion —
    // le contraire de sa raison d'être. Seul le ping santé fait foi.
    if (!isDesktopApp() && !navigator.onLine) {
      markOffline();
      return;
    }
    const reachable = await pingApiHealth();
    if (reachable) {
      setLastOnlineAt(Date.now());
      setIsOnline(true);
      onlineManager.setOnline(true);
      if (wasOfflineRef.current) {
        wasOfflineRef.current = false;
        // Auto-sync when coming back online
        syncNow();
      }
    } else {
      markOffline();
    }
  }, [markOffline, syncNow]);

  useEffect(() => {
    const handleOnline = () => {
      setIsNetworkOnline(true);
      // Browser reports a network — confirm the API is actually reachable
      // before flipping to online (captive-portal guard).
      void verifyConnectivity();
    };

    const handleOffline = () => {
      setIsNetworkOnline(false);
      // Desktop : l'événement `offline` signale la perte du réseau, pas
      // celle de l'API locale — re-vérifier au lieu de couper aveuglément.
      if (isDesktopApp()) {
        void verifyConnectivity();
        return;
      }
      markOffline();
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial state: verify instead of trusting navigator.onLine
    void verifyConnectivity();

    // Periodic re-verification (both directions: detects an API that
    // silently became unreachable, and recovery after a captive portal).
    // Also re-derives the queue counts: `held` follows the active
    // session, not the stored records (see refreshCounts).
    const interval = window.setInterval(() => {
      void verifyConnectivity();
      void refreshCounts();
    }, HEALTH_PING_INTERVAL_MS);

    // Load initial queue counts
    void refreshCounts();

    // Listen for sync queue changes (from API client queuing mutations)
    const unsub = onSyncQueueChange(applyCounts);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.clearInterval(interval);
      unsub();
    };
  }, [verifyConnectivity, markOffline, refreshCounts, applyCounts]);

  return (
    <OfflineContext.Provider
      value={{
        isOnline,
        isOffline: !isOnline,
        isNetworkOnline,
        isSyncing,
        pendingCount,
        failedCount,
        heldCount,
        lastOnlineAt,
        syncNow,
      }}
    >
      {children}
    </OfflineContext.Provider>
  );
}
